'use client';

import { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Stack,
  Button,
  Switch,
  FormControl,
  FormControlLabel,
  FormGroup,
  TextField,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Grid,
  InputAdornment,
  Paper,
  ButtonBase,
  Tabs,
  Tab,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { toast } from 'react-toastify';
import {
  ArrowLeft,
  Settings,
  Gift,
  Database,
  Users,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { getReferelInfo, updateReferelnfo } from '../api/users.api';
import {
  getAppSetting,
  updateAppSetting,
} from '../api/services';
import { isAdminOrOwner } from '../utils/hasPermission';

import SettingCard from './components/SettingCard';
import ModalC from './components/CModal';

const settingsCategories = [
  {
    id: 'maintenance-mode',
    title: 'Maintenance Mode',
    description: 'Control application availability during maintenance.',
    icon: <Settings size={24} />,
  },
  {
    id: 'promotions',
    title: 'Promotions',
    description: 'Manage promotional content and campaigns.',
    icon: <Gift size={24} />,
  },
  {
    id: 'data-manual-services',
    title: 'Data & Manual Services',
    description: 'Configure manual data limits and other manual services.',
    icon: <Database size={24} />,
  },
  {
    id: 'referral-program',
    title: 'Referral Program',
    description: 'Define rules and rewards for your referral program.',
    icon: <Users size={24} />,
  },
];

const formatDateTimeLocal = (dateString) => {
  if (!dateString) return '';
  try {
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return '';
    const tzOffset = date.getTimezoneOffset() * 60000;
    return new Date(date.getTime() - tzOffset).toISOString().slice(0, 16);
  } catch (error) {
    return '';
  }
};

export default function AppSystemSetting() {
  const navigate = useNavigate();
  const { user, activeApp } = useSelector((state) => state.auth);
  const isPrivileged = isAdminOrOwner(user, activeApp);
  const [currentView, setCurrentView] = useState('main'); // Controls which section is displayed

  // All state variables from the previous version
  const [state, setState] = useState({
    service: false,
  });
  const [manualDataLimit, setManualDataLimit] = useState(undefined);
  const [referelInfo, setReferelInfo] = useState({});
  const [updateReferelInfoModal, setUpdateReferelInfoModal] = useState(false);
  const [referelUpdateLoading, setReferelUpdateLoading] = useState(false);
  const [dataLimitUpdateLoading, setDataLimitUpdateLoading] = useState(false);
  const [appSetting, setAppSetting] = useState(null);
  const [updateManualDataLimitModal, setUpdateManualDataLimitModal] = useState(false);
  const [referralSettings, setReferralSettings] = useState({
    enabled: false,
    trigger: 'first_deposit',
    type: 'percentage',
    value: 10,
    maxReward: 1000,
    minWithdrawableAmount: 500,
    isServiceReferralEnabled: false,
    serviceReferralStartAt: '',
    depositReferralStartAt: '',
  });
  const [referralSettingsLoading, setReferralSettingsLoading] = useState(false);
  const [referralTab, setReferralTab] = useState(0);
  const [defaultCampaignRedirectUrl, setDefaultCampaignRedirectUrl] = useState('');
  const [androidPackageId, setAndroidPackageId] = useState('');
  const [iosAppId, setIosAppId] = useState('');
  const [webAppBaseUrl, setWebAppBaseUrl] = useState('');
  const [campaignRedirectUpdateLoading, setCampaignRedirectUpdateLoading] = useState(false);

  // All handlers from the previous version
  const toggleUpdateReferelInfo = () => setUpdateReferelInfoModal(!updateReferelInfoModal);
  const toggleUpdateManualDataLimit = () => setUpdateManualDataLimitModal(!updateManualDataLimitModal);

  const handleChangeReferralSettings = (e) => {
    const { name, value, type, checked } = e.target;
    setReferralSettings((prev) => ({
      ...prev,
      [name]: type === 'checkbox' || name === 'enabled' || name === 'isServiceReferralEnabled' ? checked : value,
    }));
  };

  const handleSaveReferralSettings = async () => {
    setReferralSettingsLoading(true);
    const request = await updateAppSetting({ referralSettings });
    if (request.ok) {
      toast.success('Referral settings updated successfully!');
      setReferralSettingsLoading(false);
      await handleGetAppSetting();
    } else {
      toast.error(request.data?.message || 'Failed to update referral settings.');
    }
    setReferralSettingsLoading(false);
  };

  const handleChangeReferalInfo = (e) => {
    if (e.target.name === 'status') {
      setReferelInfo({ ...referelInfo, [e.target.name]: e.target.checked });
      return;
    }
    setReferelInfo({ ...referelInfo, [e.target.name]: e.target.value });
  };

  const handleGetAppSetting = async () => {
    const request = await getAppSetting();
    if (request.ok) {
      const { data } = request.data;
      setAppSetting(data);
      setDefaultCampaignRedirectUrl(data?.defaultCampaignRedirectUrl || '');
      setAndroidPackageId(data?.androidPackageId || '');
      setIosAppId(data?.iosAppId || '');
      setWebAppBaseUrl(data?.webAppBaseUrl || '');
      if (data?.referralSettings) {
        setReferralSettings({
          enabled: false,
          trigger: 'first_deposit',
          type: 'percentage',
          value: 10,
          maxReward: 1000,
          minWithdrawableAmount: 500,
          isServiceReferralEnabled: false,
          serviceReferralStartAt: '',
          depositReferralStartAt: '',
          ...data.referralSettings,
        });
      }
    }
  };

  const handleUpdateAppSetting = async () => {
    setDataLimitUpdateLoading(true);
    const request = await updateAppSetting({ manualDataLimit });
    if (request.ok) {
      await handleGetAppSetting();
      toast.success('Data limit updated successfully');
      setDataLimitUpdateLoading(false);
      toggleUpdateManualDataLimit();
      setManualDataLimit(undefined);
      return;
    }
    setDataLimitUpdateLoading(false);
    toast.error(request.data?.message || 'Unable to update data limit');
  };

  const handleSaveCampaignRedirectUrl = async () => {
    if (!defaultCampaignRedirectUrl.trim()) {
      toast.warning('Default redirection URL cannot be empty');
      return;
    }
    setCampaignRedirectUpdateLoading(true);
    const request = await updateAppSetting({
      defaultCampaignRedirectUrl: defaultCampaignRedirectUrl.trim(),
      androidPackageId: androidPackageId.trim(),
      iosAppId: iosAppId.trim(),
      webAppBaseUrl: webAppBaseUrl.trim(),
    });
    if (request.ok) {
      await handleGetAppSetting();
      toast.success('App redirection targets updated successfully');
    } else {
      toast.error(request.data?.message || 'Update failed');
    }
    setCampaignRedirectUpdateLoading(false);
  };

  const handleGetReferelInfo = async () => {
    const request = await getReferelInfo();
    if (request.ok) {
      setReferelInfo(request.data.data);
    }
  };

  const handleUpdateReferelInfo = async () => {
    setReferelUpdateLoading(true);
    const request = await updateReferelnfo(referelInfo);
    if (request.ok) {
      toggleUpdateReferelInfo();
      setReferelUpdateLoading(false);
      return;
    }
    toast.error(request.data ? request.data.message : 'Unable to complete your request. Please try again.');
    setReferelUpdateLoading(false);
  };

  // Initial data fetching
  useEffect(() => {
    if (!isPrivileged) return;
    handleGetAppSetting();
    handleGetReferelInfo();
  }, [isPrivileged]);

  // Render logic based on currentView
  const renderSettingsContent = () => {
    const currentCategory = settingsCategories.find((cat) => cat.id === currentView);

    return (
      <Paper
        elevation={0}
        sx={{
          borderRadius: 3,
          boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -1px rgb(0 0 0 / 0.06)',
          border: '1px solid',
          borderColor: 'grey.200',
          bgcolor: 'white',
          overflow: 'hidden',
          p: { xs: 2, md: 3 },
        }}
      >
        <Stack spacing={3}>
          <Box>
            <Typography variant="h5" fontWeight={700} color="text.primary">
              {currentCategory.title}
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mt: 0.5 }}>
              {currentCategory.description}
            </Typography>
          </Box>

          {currentView === 'maintenance-mode' && (
            <SettingCard>
              <Stack spacing={2}>
                <Typography variant="h6" fontWeight="700">
                  Maintenance Mode
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  If you need to take your app offline, you can turn on Maintenance mode.
                </Typography>
                <FormControl component="fieldset">
                  <FormGroup>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={state.service}
                          onChange={(e) => setState({ ...state, service: e.target.checked })}
                          name="service"
                        />
                      }
                      label={`Maintenance mode is ${state.service ? 'ON' : 'OFF'}`}
                    />
                  </FormGroup>
                </FormControl>
              </Stack>
            </SettingCard>
          )}

          {currentView === 'promotions' && (
            <Stack spacing={3}>
              <SettingCard>
                <Stack spacing={2}>
                  <Typography variant="h6" fontWeight="700">
                    Promo Content Settings
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Setup promo content such as for the Referral Program page.
                  </Typography>
                </Stack>
                <Paper
                  sx={{
                    padding: { xs: '10px', md: '20px' },
                    minHeight: 100,
                    mt: 2,
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'grey.200',
                    boxShadow: 'none',
                  }}
                >
                  <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Typography variant="body1" fontWeight={500}>
                      Referral Program Content
                    </Typography>
                    <Button onClick={toggleUpdateReferelInfo} variant="outlined" sx={{ borderRadius: 2 }}>
                      View
                    </Button>
                  </Stack>
                </Paper>
              </SettingCard>

              <SettingCard>
                <Stack spacing={2}>
                  <Typography variant="h6" fontWeight="700">
                    Default Campaign Redirect URL
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Configure the fallback landing page or play store destination when users access downloads.
                  </Typography>
                </Stack>
                <Paper
                  sx={{
                    padding: { xs: '20px', md: '30px' },
                    mt: 2,
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'grey.200',
                    boxShadow: 'none',
                  }}
                >
                  <Stack spacing={3}>
                    <TextField
                      fullWidth
                      label="Campaign Base Redirection URL"
                      placeholder="https://yourdomain.com/download"
                      value={defaultCampaignRedirectUrl}
                      onChange={(e) => setDefaultCampaignRedirectUrl(e.target.value)}
                    />
                    <TextField
                      fullWidth
                      label="Web App Base URL"
                      placeholder="e.g. https://app.yourdomain.com"
                      value={webAppBaseUrl}
                      onChange={(e) => setWebAppBaseUrl(e.target.value)}
                    />
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="Android Package ID"
                          placeholder="e.g. com.tenant.app"
                          value={androidPackageId}
                          onChange={(e) => setAndroidPackageId(e.target.value)}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="iOS App Store ID"
                          placeholder="e.g. id6739472659"
                          value={iosAppId}
                          onChange={(e) => setIosAppId(e.target.value)}
                        />
                      </Grid>
                    </Grid>
                    <Stack direction="row" justifyContent="flex-end">
                      <LoadingButton
                        loading={campaignRedirectUpdateLoading}
                        onClick={handleSaveCampaignRedirectUrl}
                        variant="contained"
                        sx={{ borderRadius: 2 }}
                      >
                        Save Settings
                      </LoadingButton>
                    </Stack>
                  </Stack>
                </Paper>
              </SettingCard>
            </Stack>
          )}

          {currentView === 'data-manual-services' && (
            <SettingCard>
              <Stack spacing={2}>
                <Typography variant="h6" fontWeight="700">
                  Manual Service
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Setup manual service settings such as updating the data limit.
                </Typography>
              </Stack>
              <Paper
                sx={{
                  padding: { xs: '10px', md: '20px' },
                  minHeight: 100,
                  mt: 2,
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'grey.200',
                  boxShadow: 'none',
                }}
              >
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Typography variant="body1" fontWeight={500}>
                    Update Data Limit
                  </Typography>
                  <Button onClick={toggleUpdateManualDataLimit} variant="outlined" sx={{ borderRadius: 2 }}>
                    View
                  </Button>
                </Stack>
              </Paper>
            </SettingCard>
          )}

          {currentView === 'referral-program' && (
            <SettingCard>
              <Stack spacing={3}>
                <Typography variant="h6" fontWeight="700">
                  Referral Program Settings
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Configure the rules and rewards for your referral program.
                </Typography>

                <Tabs
                  value={referralTab}
                  onChange={(e, val) => setReferralTab(val)}
                  sx={{
                    borderBottom: 1,
                    borderColor: 'divider',
                    mb: 2,
                    '& .MuiTab-root': {
                      fontWeight: 600,
                      textTransform: 'none',
                      fontSize: '0.95rem',
                    },
                  }}
                >
                  <Tab label="Deposit Referral" />
                  <Tab label="Service Referral" />
                </Tabs>

                {referralTab === 0 && (
                  <Stack spacing={3}>
                    <FormGroup>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={referralSettings.enabled}
                            onChange={handleChangeReferralSettings}
                            name="enabled"
                          />
                        }
                        label={`Deposit Referral is ${referralSettings.enabled ? 'ON' : 'OFF'}`}
                      />
                    </FormGroup>
                    {referralSettings.enabled && (
                      <Grid container spacing={3}>
                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth
                            label="Referral Starting Date"
                            name="depositReferralStartAt"
                            type="datetime-local"
                            value={formatDateTimeLocal(referralSettings.depositReferralStartAt)}
                            onChange={handleChangeReferralSettings}
                            InputLabelProps={{ shrink: true }}
                            helperText="Only users created on or after this date will qualify for deposit referrals."
                            variant="outlined"
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                borderRadius: 2,
                              },
                            }}
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <FormControl fullWidth>
                            <InputLabel>Reward Trigger</InputLabel>
                            <Select
                              name="trigger"
                              value={referralSettings.trigger}
                              label="Reward Trigger"
                              onChange={handleChangeReferralSettings}
                              sx={{ borderRadius: 2 }}
                            >
                              <MenuItem value="first_deposit">First Deposit</MenuItem>
                              <MenuItem value="all_deposits">All Deposits</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <FormControl fullWidth>
                            <InputLabel>Reward Type</InputLabel>
                            <Select
                              name="type"
                              value={referralSettings.type}
                              label="Reward Type"
                              onChange={handleChangeReferralSettings}
                              sx={{ borderRadius: 2 }}
                            >
                              <MenuItem value="percentage">Percentage</MenuItem>
                              <MenuItem value="constant">Constant</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth
                            label="Reward Value"
                            name="value"
                            type="number"
                            value={referralSettings.value}
                            onChange={handleChangeReferralSettings}
                            variant="outlined"
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                borderRadius: 2,
                              },
                            }}
                            InputProps={{
                              endAdornment: (
                                <InputAdornment position="end">
                                  {referralSettings.type === 'percentage' ? '%' : 'NGN'}
                                </InputAdornment>
                              ),
                            }}
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth
                            label="Maximum Reward (Optional)"
                            name="maxReward"
                            type="number"
                            value={referralSettings.maxReward}
                            onChange={handleChangeReferralSettings}
                            helperText="Set to 0 for no limit. Applies only to percentage-based rewards."
                            variant="outlined"
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                borderRadius: 2,
                              },
                            }}
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth
                            label="Minimum Withdrawable Amount (Optional)"
                            name="minWithdrawableAmount"
                            type="number"
                            value={referralSettings.minWithdrawableAmount}
                            onChange={handleChangeReferralSettings}
                            helperText="Minimum referral earnings a user must have to withdraw. Set to 0 for no minimum."
                            variant="outlined"
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                borderRadius: 2,
                              },
                            }}
                          />
                        </Grid>
                      </Grid>
                    )}
                  </Stack>
                )}

                {referralTab === 1 && (
                  <Stack spacing={3}>
                    <FormGroup>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={referralSettings.isServiceReferralEnabled}
                            onChange={handleChangeReferralSettings}
                            name="isServiceReferralEnabled"
                          />
                        }
                        label={`Service Referral is ${referralSettings.isServiceReferralEnabled ? 'ON' : 'OFF'}`}
                      />
                    </FormGroup>
                    {referralSettings.isServiceReferralEnabled && (
                      <Grid container spacing={3}>
                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth
                            label="Referral Starting Date"
                            name="serviceReferralStartAt"
                            type="datetime-local"
                            value={formatDateTimeLocal(referralSettings.serviceReferralStartAt)}
                            onChange={handleChangeReferralSettings}
                            InputLabelProps={{ shrink: true }}
                            helperText="Only users created on or after this date will qualify for service referrals."
                            variant="outlined"
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                borderRadius: 2,
                              },
                            }}
                          />
                        </Grid>
                      </Grid>
                    )}
                  </Stack>
                )}

                <Box sx={{ mt: 3 }}>
                  <LoadingButton
                    variant="contained"
                    loading={referralSettingsLoading}
                    onClick={handleSaveReferralSettings}
                    sx={{ borderRadius: 2 }}
                  >
                    Save Referral Settings
                  </LoadingButton>
                </Box>
              </Stack>
            </SettingCard>
          )}
        </Stack>
      </Paper>
    );
  };

  if (!isPrivileged) {
    return (
      <Box sx={{ p: 4, textAlign: 'center', maxWidth: 540, mx: 'auto', mt: 4 }}>
        <Paper variant="outlined" sx={{ p: 5, borderRadius: 4, textAlign: 'center' }}>
          <Typography variant="h5" color="error" fontWeight={800} sx={{ mb: 1.5 }}>
            Access Denied
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            System configuration, vendor processor API keys, and maintenance controls are restricted to workspace owners and administrators only.
          </Typography>
          <Button
            onClick={() => navigate('/dashboard/settings')}
            variant="contained"
            sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 700 }}
          >
            Go to Settings
          </Button>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'grey.50', p: { xs: 2, md: 4 } }}>
      <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: { xs: 2, md: 4 } }}>
          {currentView === 'main' ? (
            <IconButton
              onClick={() => navigate('/dashboard/settings')}
              sx={{
                bgcolor: 'grey.100',
                '&:hover': { bgcolor: 'grey.200' },
              }}
            >
              <ArrowLeft size={20} />
            </IconButton>
          ) : (
            <IconButton
              onClick={() => setCurrentView('main')}
              sx={{
                bgcolor: 'grey.100',
                '&:hover': { bgcolor: 'grey.200' },
              }}
            >
              <ArrowLeft size={20} />
            </IconButton>
          )}
          <Typography variant="h5" fontWeight={700} color="text.primary">
            {currentView === 'main'
              ? 'Application Settings'
              : settingsCategories.find((cat) => cat.id === currentView)?.title}
          </Typography>
        </Stack>

        {currentView === 'main' ? (
          <Grid container spacing={3}>
            {settingsCategories.map((category) => (
              <Grid item xs={12} sm={6} md={4} key={category.id}>
                <Paper
                  component={ButtonBase}
                  onClick={() => setCurrentView(category.id)}
                  sx={{
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    p: 3,
                    borderRadius: 3,
                    boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -1px rgb(0 0 0 / 0.06)',
                    border: '1px solid',
                    borderColor: 'grey.200',
                    textAlign: 'left',
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      bgcolor: 'grey.100',
                      borderColor: 'primary.main',
                      boxShadow: '0 6px 12px -2px rgb(0 0 0 / 0.15), 0 3px 6px -2px rgb(0 0 0 / 0.08)',
                      transform: 'translateY(-2px)',
                    },
                  }}
                >
                  <Box sx={{ color: 'primary.main', mb: 2 }}>{category.icon}</Box>
                  <Typography variant="h6" fontWeight={600} color="text.primary" sx={{ mb: 1 }}>
                    {category.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {category.description}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        ) : (
          renderSettingsContent()
        )}


        <ModalC isOpen={updateReferelInfoModal} setOpen={toggleUpdateReferelInfo}>
          <Stack spacing={3}>
            <Typography variant="h6" fontWeight={600}>
              Setup your Referral Program
            </Typography>
            <FormGroup>
              <FormControlLabel
                control={<Switch checked={referelInfo.status} onChange={handleChangeReferalInfo} name="status" />}
                label={`Referral Program is ${referelInfo.status ? 'ON' : 'OFF'}`}
              />
            </FormGroup>
            {referelInfo.status ? (
              <>
                <TextField
                  label="Referral Program title"
                  name="title"
                  value={referelInfo.title}
                  onChange={handleChangeReferalInfo}
                  placeholder="Give the referral program title"
                  variant="outlined"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    },
                  }}
                />
                <TextField
                  label="Referral Program descriptions"
                  multiline
                  rows={4}
                  name="subtitle"
                  value={referelInfo.subtitle}
                  onChange={handleChangeReferalInfo}
                  placeholder="Add referral program info"
                  variant="outlined"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    },
                  }}
                />
              </>
            ) : (
              <>
                <TextField
                  label="Referral Program end title"
                  name="statusTitle"
                  value={referelInfo.statusTitle}
                  onChange={handleChangeReferalInfo}
                  placeholder="Referral program end title"
                  variant="outlined"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    },
                  }}
                />
                <TextField
                  label="Referral Program end descriptions"
                  multiline
                  rows={4}
                  name="statusSubtitle"
                  value={referelInfo.statusSubtitle}
                  onChange={handleChangeReferalInfo}
                  placeholder="Referral program end descriptions"
                  variant="outlined"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    },
                  }}
                />
              </>
            )}
            <LoadingButton
              onClick={handleUpdateReferelInfo}
              loading={referelUpdateLoading}
              variant="contained"
              sx={{ borderRadius: 2 }}
            >
              Update
            </LoadingButton>
            <Button
              variant="outlined"
              onClick={toggleUpdateReferelInfo}
              disabled={referelUpdateLoading}
              sx={{ borderRadius: 2 }}
            >
              Cancel
            </Button>
          </Stack>
        </ModalC>

        <ModalC isOpen={updateManualDataLimitModal} setOpen={toggleUpdateManualDataLimit}>
          <Stack spacing={3}>
            <Typography variant="h6" fontWeight={600}>
              Update Manual Data Limit
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Current Data Balance: {appSetting?.manualDataLimit}MB
            </Typography>
            <TextField
              fullWidth
              label="Manual Data Limit"
              type="number"
              value={manualDataLimit}
              onChange={(event) => {
                if (Number(event.target.value) || event.target.value === '') {
                  setManualDataLimit(event.target.value);
                }
              }}
              variant="outlined"
              sx={{
                my: '20px',
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                },
              }}
            />
            <LoadingButton
              onClick={handleUpdateAppSetting}
              loading={dataLimitUpdateLoading}
              variant="contained"
              sx={{ borderRadius: 2 }}
            >
              Update
            </LoadingButton>
            <Button
              variant="outlined"
              onClick={toggleUpdateManualDataLimit}
              disabled={dataLimitUpdateLoading}
              sx={{ borderRadius: 2 }}
            >
              Cancel
            </Button>
          </Stack>
        </ModalC>
      </Box>
    </Box>
  );
}
